/*
 * @format
 */

import { object, ref, string, number, date } from 'yup';

const valid = () => ({
  valid: true,
  error: undefined,
});

const invalid = validationError => ({
  valid: false,
  error: validationError.errors[0],
});

const showMessage = message => ({
  valid: false,
  error: message,
});

export const DEFAULT_VALIDATION = valid();

const emailValidationSchema = string().label('Email').email().required();
export const emailValidator = async email => {
  try {
    await emailValidationSchema.validate(email);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

const emailMatchValidationSchema = object({
  email: string()
    .label('email')
    .oneOf([ref('accountEmail'), null], 'Invalid email address'),
});
export const emailMatchesAccountEmail = async (email, accountEmail) => {
  try {
    await emailValidationSchema.validate(email);
    await emailMatchValidationSchema.validate({ email, accountEmail });

    return valid();
  } catch (error) {
    return invalid(error);
  }
};

export const firstNameValidationSchema = string()
  .label('First Name')
  .min(2)
  .max(40)
  .required();
export const firstNameValidator = async firstName => {
  try {
    await firstNameValidationSchema.validate(firstName);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

export const lastNameValidationSchema = string()
  .label('Last Name')
  .min(2)
  .max(40)
  .required();
export const lastNameValidator = async lastName => {
  try {
    await lastNameValidationSchema.validate(lastName);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

export const passwordValidationSchema = string()
  .label('Password')
  .min(8)
  .max(24)
  .matches(
    /^(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`])(?=.*\d).*$/,
    'Passwords must contain an uppercase, lowercase, numeric and special character',
  )
  .required();
export const passwordValidator = async password => {
  try {
    await passwordValidationSchema.validate(password);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

export const passwordConfirmationValidationSchema = object({
  passwordConfirmation: string()
    .label('Password confirmation')
    .oneOf([ref('password'), null], 'Passwords must match')
    .required(),
});

export const passwordResetValidationSchema = object({
  newPassword: string()
    .label('New password')
    .notOneOf(
      [ref('oldPassword'), null],
      'Your new password should not match your old one',
    )
    .required(),
});
export const passwordUpdateValidator = async (oldPassword, newPassword) => {
  try {
    await passwordValidationSchema.validate(newPassword);
    await passwordResetValidationSchema.validate({
      oldPassword,
      newPassword,
    });
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

export const passwordConfirmationValidator = async (
  password,
  passwordConfirmation,
) => {
  try {
    await passwordConfirmationValidationSchema.validate({
      password,
      passwordConfirmation,
    });
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

const cognitoCodeValidationSchema = string()
  .typeError('The code must be a 6 digit number')
  .label('Code')
  .test(
    'length',
    'The code must be a 6 digit number',
    x => x.toString().length === 6,
  )
  .required();
export const cognitoCodeValidator = async code => {
  try {
    await cognitoCodeValidationSchema.validate(code);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

const scheduleStartTimeValidationSchema = existingStartTimes =>
  number()
    .label('Start Time')
    .notOneOf(
      existingStartTimes,
      'A Heating Event with this start time already exists',
    )
    .required();
export const scheduleStartTimeValidator = (existingStartTimes, startTime) => {
  try {
    scheduleStartTimeValidationSchema(existingStartTimes).validateSync(
      startTime,
    );
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

const siFriendlyNameValidationSchema = homeNames =>
  string()
    .label('Name')
    .notOneOf(homeNames, 'Your friendly name must be unique')
    .min(4)
    .max(50)
    .required();
export const siFriendlyNameValidator = async (takenNames, name) => {
  try {
    await siFriendlyNameValidationSchema(takenNames).validateSync(name);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

const serialNumberValidationSchema = string()
  .label('Serial Number')
  .matches(
    /^[a-zA-Z0-9]*$/,
    'Your serial number must contain only letters and numbers',
  )
  .required();
export const serialNumberValidator = async serialNumber => {
  try {
    await serialNumberValidationSchema.validate(serialNumber);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

export const resendCodeValidator = (show, message) => {
  if (show) {
    return showMessage(message);
  }
  return valid();
};

const geolocationRadiusSchema = number().min(500).max(99999).required();

export const geolocationRadiusValidator = async radius => {
  try {
    await geolocationRadiusSchema.validate(radius);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

const multiZoneNameValidationSchema = string()
  .label('Zone Name')
  .min(1)
  .max(6)
  .matches(
    /^[a-zA-Z0-9\s]*$/,
    'Zone Name must contain only letters, numbers and spaces',
  );
export const multiZoneNameValidator = async zoneName => {
  try {
    await multiZoneNameValidationSchema.validate(zoneName);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

// matches the PRT validation
const phoneNumberValidationSchema = string()
  .min(3)
  .max(12)
  .matches(/^[0-9]*$/, 'must only contain numbers')
  .required();
export const phoneNumberValidator = async phoneNumber => {
  try {
    await phoneNumberValidationSchema.validate(phoneNumber);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

const renameThermostatValidationSchema = string()
  .label('Name')
  .min(1)
  .max(6)
  .matches(/^[a-zA-Z0-9\s]*$/, 'Must contain only letters, numbers and spaces');
export const thermostatRenameValidator = async thermostatName => {
  try {
    await renameThermostatValidationSchema.validate(thermostatName);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

export const holidayEndTimeValidator = endTime => {
  // Bounds are computed per call, NOT once at module load. This module is
  // evaluated a single time (app launch on mobile; page load in the web SPA), so
  // module-level min/max consts drift out of date across a long-lived session — a
  // date the picker shows as in-range would be silently rejected, and after ~90
  // days the accepted window would fall entirely in the past. Rebuilding the schema
  // here keeps the window anchored to "now" on every validation.
  const now = new Date();
  const holidayMinDate = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const holidayMaxDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
  const holidayEndTimeValidationSchema = date()
    .label('End Time')
    .min(holidayMinDate)
    .max(holidayMaxDate)
    .required();
  try {
    holidayEndTimeValidationSchema.validateSync(endTime);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};

export const deviceNameValidationSchema = string()
  .label('Device Name')
  .min(1)
  .max(24)
  .required();
export const deviceNameValidator = async deviceName => {
  try {
    await deviceNameValidationSchema.validate(deviceName);
    return valid();
  } catch (error) {
    return invalid(error);
  }
};
